import { sectionHeading, gameColors, woodPanel, metalPanel, copperButton } from '@/shared/ui/game-theme';
import { GameIcon, type GameIconName } from '@/shared/ui/game-icon';
import { useState, useCallback, useEffect, type CSSProperties } from 'react';
import type { HeroQuest } from '@/game/types';
import { useT, useLocale } from '@/i18n';
import { localizedQuestText } from '@/i18n/questCopy';
import { townDisplayById } from '@/i18n/contentLabels';

// ---- Types ----

interface QuestLogProps {
  quests: HeroQuest[];
  onClaim: (heroQuestId: number) => void;
  onAbandon: (heroQuestId: number) => void;
  onClose: () => void;
  claimDisabled?: boolean;
}

// ---- Quest Type Icons ----

function questTypeIcon(type: string): GameIconName {
  switch (type) {
    case 'kill_count':
      return 'swords'; // swords
    case 'visit_town':
      return 'map'; // compass
    case 'collect_item':
      return 'gem'; // gem
    default:
      return 'scroll'; // scroll
  }
}

// ---- Styles ----

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 600,
  pointerEvents: 'auto',
};

const backdropStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
};

const panelStyle: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: '40vh',
  backgroundColor: 'rgba(15, 15, 25, 0.95)',
  borderTop: '2px solid rgba(255, 215, 0, 0.3)',
  borderRadius: '16px 16px 0 0',
  display: 'flex',
  flexDirection: 'column',
  animation: 'quest-log-slide-up 0.3s ease-out',
  overflow: 'hidden',
  ...woodPanel,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px 8px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

const titleStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: gameColors.gold,
  textShadow: '0 1px #171919',
  ...sectionHeading,
};

const closeBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: gameColors.faint,
  fontSize: 20,
  cursor: 'pointer',
  padding: '4px 8px',
  lineHeight: 1,
  ...copperButton,
};

const listStyle: CSSProperties = {
  overflowY: 'auto',
  padding: '8px 12px 16px',
  flex: 1,
};

const questCardStyle: CSSProperties = {
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 10,
  padding: '10px 12px',
  marginBottom: 8,
  cursor: 'pointer',
  transition: 'background-color 150ms ease',
  ...metalPanel,
};

const questHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const questTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: gameColors.text,
  flex: 1,
};

const progressTextStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: gameColors.muted,
  whiteSpace: 'nowrap',
};

const progressBarOuter: CSSProperties = {
  height: 6,
  borderRadius: 3,
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  marginTop: 6,
  overflow: 'hidden',
};

const descriptionStyle: CSSProperties = {
  fontSize: 11,
  color: gameColors.faint,
  marginTop: 8,
  lineHeight: 1.4,
};

const rewardsRow: CSSProperties = {
  display: 'flex',
  gap: 10,
  marginTop: 8,
  fontSize: 11,
  fontWeight: 600,
};

const rewardChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
};

const actionRow: CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 10,
};

const claimBtnStyle: CSSProperties = {
  flex: 1,
  padding: '8px 0',
  border: 'none',
  borderRadius: 8,
  backgroundColor: 'rgba(255, 215, 0, 0.25)',
  color: gameColors.gold,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  textShadow: '0 1px #171919',
  boxShadow: '0 0 12px rgba(255, 215, 0, 0.15)',
  animation: 'none',
  ...copperButton,
};

const claimBtnDisabledStyle: CSSProperties = {
  ...claimBtnStyle,
  opacity: 0.45,
  cursor: 'not-allowed',
  animation: 'none',
  boxShadow: 'none',
  textShadow: 'none',
};

const abandonBtnStyle: CSSProperties = {
  padding: '8px 14px',
  border: '1px solid rgba(255, 80, 80, 0.3)',
  borderRadius: 8,
  backgroundColor: 'rgba(255, 50, 50, 0.1)',
  color: gameColors.danger,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  ...copperButton,
};

const emptyStyle: CSSProperties = {
  textAlign: 'center',
  color: gameColors.faint,
  fontSize: 13,
  padding: '24px 0',
};

interface QuestLogListProps {
  /** The sheet owns scrolling; standalone panels retain a bounded list. */
  embedded?: boolean;
  quests: HeroQuest[];
  onClaim: (heroQuestId: number) => void;
  onAbandon: (heroQuestId: number) => void;
  /** When true, completed-quest claim is blocked (e.g. hero dead). */
  claimDisabled?: boolean;
}

/** Quest list body (embedded in Hero sheet or standalone panel). */
export function QuestLogList({ quests, onClaim, onAbandon, claimDisabled = false, embedded = false }: QuestLogListProps) {
  const tr = useT();
  const { locale } = useLocale();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const activeQuests = quests.filter((q) => q.status !== 'claimed');

  const handleCardClick = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <>
      <div
        className="th-quest-list"
        style={{
          ...listStyle,
          padding: '0 0 8px',
          maxHeight: embedded ? undefined : 'min(52vh, 400px)',
          overflowY: embedded ? 'visible' : 'auto',
          minHeight: embedded ? '100%' : undefined,
        }}
      >
            {activeQuests.length === 0 ? (
              <div style={emptyStyle}>{tr.noActiveQuests}</div>
            ) : (
              activeQuests.map((q) => {
                const isExpanded = expandedId === q.id;
                const isCompleted = q.status === 'completed';
                const pct = q.targetCount > 0 ? Math.min(100, (q.progress / q.targetCount) * 100) : 0;

                return (
                  <div
                    key={q.id}
                    style={{
                      ...questCardStyle,
                      borderColor: isCompleted ? gameColors.gold : gameColors.edge,
                    }}
                    onClick={() => handleCardClick(q.id)}
                  >
                    <div style={questHeaderRow}>
                      <span style={{ fontSize: 16 }}><GameIcon name={questTypeIcon(q.type)} color={gameColors.copper} /></span>
                      <span style={questTitleStyle}>
                        {localizedQuestText(locale, q.questKey, 'title', q.title)}
                      </span>
                      <span
                        style={{
                          ...progressTextStyle,
                          color: isCompleted ? gameColors.gold : gameColors.muted,
                        }}
                      >
                        {q.progress}/{q.targetCount}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div style={progressBarOuter}>
                      <div
                        style={{
                          height: '100%',
                          width: `${pct}%`,
                          borderRadius: 3,
                          backgroundColor: isCompleted ? gameColors.gold : gameColors.blue,
                          transition: 'width 300ms ease',
                        }}
                      />
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <>
                        <div style={descriptionStyle}>
                          {localizedQuestText(locale, q.questKey, 'description', q.description)}
                        </div>
                        {q.type === 'visit_town' && (q.targetTownId != null || q.targetTownName) ? (
                          <div style={{ ...descriptionStyle, color: gameColors.blue, fontSize: 11 }}>
                            {tr.questDestination}:{' '}
                            {townDisplayById(
                              locale,
                              q.targetTownId,
                              q.targetTownName ?? '',
                            )}
                          </div>
                        ) : null}
                        <div style={{ ...descriptionStyle, color: gameColors.faint, fontSize: 12 }}>
                          {q.npcName} &middot; {q.townName}
                        </div>
                        <div style={rewardsRow}>
                          {q.rewardXp > 0 && (
                            <span style={{ ...rewardChip, color: gameColors.blue }}>
                              <GameIcon name="sparkles" size={16} /> {q.rewardXp} XP
                            </span>
                          )}
                          {q.rewardGold > 0 && (
                            <span style={{ ...rewardChip, color: gameColors.gold }}>
                              <GameIcon name="coins" size={16} /> {q.rewardGold}
                            </span>
                          )}
                          {q.rewardPotions > 0 && (
                            <span style={{ ...rewardChip, color: gameColors.healing }}>
                              <GameIcon name="flask" size={16} /> {q.rewardPotions}
                            </span>
                          )}
                        </div>
                        <div style={actionRow}>
                          {isCompleted ? (
                            <button
                              type="button"
                              style={claimDisabled ? claimBtnDisabledStyle : claimBtnStyle}
                              disabled={claimDisabled}
                              title={claimDisabled ? tr.claimRewardsDisabledDead : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (claimDisabled) return;
                                onClaim(q.id);
                              }}
                            >
                              {tr.claimRewards}
                            </button>
                          ) : (
                            <button
                              style={abandonBtnStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAbandon(q.id);
                              }}
                            >
                              {tr.abandon}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
      </div>
    </>
  );
}

export function QuestLog({ quests, onClaim, onAbandon, onClose, claimDisabled }: QuestLogProps) {
  const tr = useT();
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <>
      <style>{`
        @keyframes quest-log-slide-up {
          0%   { transform: translateY(100%); }
          100% { transform: translateY(0); }
        }
      `}</style>
      <div style={overlayStyle}>
        <div style={backdropStyle} onClick={onClose} />
        <div style={panelStyle}>
          <div style={headerStyle}>
            <span style={titleStyle}><GameIcon name="scroll" size={16} /> {tr.questLog}</span>
            <button style={closeBtnStyle} onClick={onClose}><GameIcon name="close" size={16} /></button>
          </div>
          <QuestLogList quests={quests} onClaim={onClaim} onAbandon={onAbandon} claimDisabled={claimDisabled} />
        </div>
      </div>
    </>
  );
}
